import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Tag, Tooltip, Button, Drawer, Form, Input, Space, Spin, Badge } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import PageFilter from '../components/page/PageFilter';
import DataCard from '../components/page/DataCard';
import { LinkOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import { taxonomyApi, errorText } from '../components/taxonomy/taxonomyApi';
import MappingMatrix from '../components/taxonomy/MappingMatrix';
import TaxonomyCoverageStrip from '../components/taxonomy/TaxonomyCoverageStrip';
import PendingFromPolicyHub from '../components/taxonomy/PendingFromPolicyHub';

function toIsoLabel(ctrlNumber) {
    const t = String(ctrlNumber || '').trim();
    if (!t) return '';
    return /^A\./i.test(t) ? `A.${t.replace(/^A\./i, '')}` : `A.${t}`;
}

/**
 * Control Taxonomy (platform admin). Cites ISO 27002:2022 cross-framework mapping: ISO 27002 controls
 * are the hub every other regulation is mapped to. Two views: the mapping matrix, and the controls
 * from Policy Hub that still need a mapping. The API enforces platform_admin; `canEdit` only hides actions.
 */
const ControlTaxonomy = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const instanceRef = useRef(instance);
    const accountRef = useRef(activeAccount);
    useEffect(() => { instanceRef.current = instance; }, [instance]);
    useEffect(() => { accountRef.current = activeAccount; }, [activeAccount]);

    const { fetchTaxonomyPendingCount, taxonomyPendingCount, user } = useApp();
    const canEdit = user?.role === Role.ADMIN;

    const getToken = useCallback(async () => {
        const req = { ...apiTokenRequest, account: accountRef.current };
        try {
            return (await instanceRef.current.acquireTokenSilent(req)).accessToken;
        } catch (err) {
            // Expired session / consent needed: a silent failure would otherwise be a permanent 401.
            if (err?.name === 'InteractionRequiredAuthError' || err?.errorCode === 'interaction_required' || err?.errorCode === 'consent_required' || err?.errorCode === 'login_required') {
                return (await instanceRef.current.acquireTokenPopup(req)).accessToken;
            }
            throw err;
        }
    }, []);
    const api = useMemo(() => taxonomyApi(getToken), [getToken]);

    const [viewMode, setViewMode] = useState('mappings');
    const [frameworks, setFrameworks] = useState(null);
    const [counts, setCounts] = useState({ iso27001: 0, frameworks: {}, total: 0 });
    const [error, setError] = useState(null);

    // ISO 27001 pending list (the pre-existing flow) and its create-mapping drawer.
    const [pendingControls, setPendingControls] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerSaving, setDrawerSaving] = useState(false);
    const [drawerError, setDrawerError] = useState(null);
    const [form] = Form.useForm();
    const [registryControlId, setRegistryControlId] = useState(null);

    const refreshMeta = useCallback(async () => {
        try {
            const [fw, pc] = await Promise.all([api.frameworks(), api.pendingCount().catch(() => null)]);
            setFrameworks(fw);
            if (pc) setCounts(pc);
            setError(null);
        } catch (err) {
            setError(errorText(err, 'Could not load the control taxonomy.'));
        }
        if (fetchTaxonomyPendingCount) fetchTaxonomyPendingCount();
    }, [api, fetchTaxonomyPendingCount]);

    useEffect(() => { refreshMeta(); }, [refreshMeta]);

    const fetchIsoPending = useCallback(async () => {
        setPendingLoading(true);
        try {
            const token = await getToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/iso27001/controls?taxonomyReviewStatus=pending`, { headers: { Authorization: `Bearer ${token}` } });
            const json = await res.json().catch(() => null);
            setPendingControls(res.ok && json?.success ? (json.data.controls || []) : []);
        } catch { setPendingControls([]); }
        finally { setPendingLoading(false); }
    }, [getToken]);

    useEffect(() => { if (viewMode === 'pending') fetchIsoPending(); }, [viewMode, fetchIsoPending]);

    const openCreateFromRegistry = useCallback((ctrl) => {
        setRegistryControlId(ctrl.id);
        setDrawerError(null);
        form.setFieldsValue({
            isoControl: toIsoLabel(ctrl.ctrlNumber),
            isoControlName: ctrl.ctrlName,
            pciDss: '', gdpr: '', nistCsf: '', iso27701: '', fedramp: '', iso42001: '', iso22301: '', nis2: '',
        });
        setDrawerOpen(true);
    }, [form]);

    const submitDrawer = async () => {
        const values = await form.validateFields();
        const body = {
            isoControl: values.isoControl.trim(),
            isoControlName: values.isoControlName.trim(),
            pciDss: values.pciDss || null, gdpr: values.gdpr || null, nistCsf: values.nistCsf || null,
            iso27701: values.iso27701 || null, fedramp: values.fedramp || null, iso42001: values.iso42001 || null,
            iso22301: values.iso22301 || null, nis2: values.nis2 || null,
        };
        if (registryControlId) body.registryControlId = registryControlId;
        setDrawerSaving(true);
        setDrawerError(null);
        try {
            const token = await getToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/control-taxonomy/mappings`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => null);
            if (!res.ok || !json?.success) throw new Error(json?.error?.message || res.statusText);
            setDrawerOpen(false);
            setRegistryControlId(null);
            await fetchIsoPending();
            await refreshMeta();
        } catch (e) {
            setDrawerError(e.message || 'Save failed');
        } finally {
            setDrawerSaving(false);
        }
    };

    const isoPendingColumns = useMemo(() => [
        { title: 'Control', dataIndex: 'ctrlNumber', key: 'ctrlNumber', width: 100, render: (n) => <Tag>{n}</Tag> },
        { title: 'Name', dataIndex: 'ctrlName', key: 'ctrlName', ellipsis: { showTitle: true } },
        { title: 'Theme', dataIndex: 'controlTheme', key: 'controlTheme', width: 140, render: (t) => (t ? <Tag>{t}</Tag> : '-') },
        {
            title: 'Action', key: 'act', width: 268, align: 'right',
            render: (_, ctrl) => (
                <Tooltip title="Map this control to PCI DSS, GDPR, NIST CSF, and other frameworks">
                    <Button type="primary" size="small" icon={<LinkOutlined />} onClick={() => openCreateFromRegistry(ctrl)}>Add cross-framework mapping</Button>
                </Tooltip>
            ),
        },
    ], [openCreateFromRegistry]);

    if (error && !frameworks) return <PageState status="error" message={error} onRetry={refreshMeta} />;
    if (!frameworks) return <PageState status="loading" message="Loading control taxonomy..." />;

    const pendingTotal = counts.total || taxonomyPendingCount || 0;

    const isoPendingNode = (
        <DataCard size="small">
            <Spin spinning={pendingLoading}>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                        className="policylens-data-table policylens-pending-taxonomy-table"
                        rowKey="id"
                        columns={isoPendingColumns}
                        dataSource={pendingControls}
                        tableLayout="fixed"
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        locale={{ emptyText: 'No controls awaiting taxonomy review' }}
                        size="small"
                    />
                </div>
            </Spin>
        </DataCard>
    );

    return (
        <PageShell width="wide">
            <PageHeader
                title="Control Taxonomy - Cross-Framework Mapping"
                subtitle="Map each ISO 27002 control to the matching controls of other regulations. Choose which regulations to compare as columns."
            />

            <Space wrap>
                <PageFilter
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                        { label: 'Cross-Framework Mapping', value: 'mappings' },
                        {
                            value: 'pending',
                            label: (
                                <Badge count={pendingTotal} size="small" offset={[10, 0]} overflowCount={999}>
                                    <span aria-label={pendingTotal > 0 ? `Pending from Policy Hub, ${pendingTotal} to review` : 'Pending from Policy Hub'}>Pending from Policy Hub</span>
                                </Badge>
                            ),
                        },
                    ]}
                />
            </Space>

            {viewMode === 'mappings' && (
                <>
                    <TaxonomyCoverageStrip frameworks={frameworks} />
                    <MappingMatrix api={api} frameworks={frameworks} canEdit={canEdit} onChanged={refreshMeta} />
                </>
            )}

            {viewMode === 'pending' && (
                <PendingFromPolicyHub api={api} frameworks={frameworks} counts={counts} isoPendingNode={isoPendingNode} onChanged={refreshMeta} />
            )}

            <Drawer
                title="Add cross-framework mapping"
                width={480}
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setRegistryControlId(null); }}
                extra={(
                    <Space>
                        <Button onClick={() => { setDrawerOpen(false); setRegistryControlId(null); }}>Cancel</Button>
                        <Button type="primary" loading={drawerSaving} onClick={submitDrawer}>Save</Button>
                    </Space>
                )}
                destroyOnClose
            >
                {drawerError && <div role="alert" style={{ color: 'var(--color-danger-fg)', marginBottom: 12 }}>{drawerError}</div>}
                <Form form={form} layout="vertical">
                    <Form.Item name="isoControl" label="ISO control ID" rules={[{ required: true }]}><Input placeholder="A.5.16" /></Form.Item>
                    <Form.Item name="isoControlName" label="Control name" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="pciDss" label="PCI DSS v4.0"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="gdpr" label="GDPR"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="nistCsf" label="NIST CSF"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="iso27701" label="ISO 27701"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="fedramp" label="FedRAMP"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="iso42001" label="ISO 42001"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="iso22301" label="ISO 22301"><Input.TextArea rows={2} /></Form.Item>
                    <Form.Item name="nis2" label="NIS2"><Input.TextArea rows={2} /></Form.Item>
                </Form>
            </Drawer>
        </PageShell>
    );
};

export default ControlTaxonomy;
